import { useEffect, useState } from "react";
import { X, ExternalLink } from "lucide-react";

const STORAGE_KEY = "sticky-cta-dismissed";

export function StickyCta({
  href,
  label,
  cta,
}: {
  href: string;
  label: string;
  cta: string;
}) {
  const [visible, setVisible] = useState(false);
  const [dismissed, setDismissed] = useState(true);

  useEffect(() => {
    if (typeof window === "undefined") return;
    try {
      setDismissed(window.localStorage.getItem(STORAGE_KEY) === "1");
    } catch {
      // ignore
    }
  }, []);

  useEffect(() => {
    if (dismissed) return;
    const onScroll = () => {
      const y = window.scrollY || window.pageYOffset;
      setVisible(y > 600);
    };
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, [dismissed]);

  const dismiss = () => {
    setVisible(false);
    setDismissed(true);
    try {
      window.localStorage.setItem(STORAGE_KEY, "1");
    } catch {
      // ignore
    }
  };

  // Signalera till andra flytande fält (t.ex. mobil ring-CTA) att botten är tagen
  useEffect(() => {
    if (dismissed) return;
    window.dispatchEvent(new CustomEvent("bottombar", { detail: { active: true } }));
    return () => {
      window.dispatchEvent(new CustomEvent("bottombar", { detail: { active: false } }));
    };
  }, [dismissed]);

  if (dismissed) return null;

  return (
    <div
      className={`fixed inset-x-0 bottom-0 z-50 transition-transform duration-300 ${
        visible ? "translate-y-0" : "translate-y-full"
      }`}
      aria-hidden={!visible}
    >
      <div className="mx-auto max-w-3xl px-4 pb-4">
        <div className="flex items-center gap-4 rounded-3xl border border-accent/25 bg-background/95 px-5 py-4 shadow-2xl backdrop-blur supports-[backdrop-filter]:bg-background/85">
          <div className="min-w-0 flex-1">
            <p className="text-xs font-medium uppercase tracking-wider text-accent">{label}</p>
            <p className="mt-0.5 text-sm font-medium leading-snug text-foreground">
              {cta}
            </p>
          </div>
          <a
            href={href}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex shrink-0 items-center gap-1.5 rounded-full bg-primary px-4 py-2.5 text-sm font-medium text-primary-foreground hover:bg-primary/90"
          >
            Se annonser <ExternalLink size={14} />
          </a>
          <button
            type="button"
            onClick={dismiss}
            className="shrink-0 rounded-full p-2 text-muted-foreground hover:bg-secondary hover:text-foreground"
            aria-label="Stäng"
          >
            <X size={18} />
          </button>
        </div>
      </div>
    </div>
  );
}
